import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { listenToBrandsByBusinessType } from "../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import appLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   CATEGORY SOURCE — strictly KitchenCategories, nothing else
═══════════════════════════════════════════════════════════ */
import {
  kitchenCategories,
  kitchenSubcategories,
  type CategoryItem,
} from "../../CATEGORIES/KitchenCategories";

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — pulled directly from the shared
   PartnerCategories registry (businessType / storeType), not
   hardcoded, so Kitchen stays in sync with that source of
   truth — exactly the same pattern BeautyHome / BabyHome use.
   Confirmed against PartnerCategories.ts: businessType "Stores"
   = id 12, storeType "Kitchen Store" = id 1220.
═══════════════════════════════════════════════════════════ */
import { businessType, storeType } from "../../CATEGORIES/PartnerCategories";

import {
  Sizes,
  ShoeSizes,
  productColors,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

import {
  Search, ShoppingBag, LayoutGrid, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, ArrowRight,
  Package, Home, ChefHat, Star, ChevronDown, Tag, Check,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   sphere helper — used for the colour-swatch variant picker
═══════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.28) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — strictly the Hearth / Copperpot palette
   (deep pine-teal like an enameled Dutch oven, brick-red like
   a tomato-braise, saffron/turmeric as the third accent) —
   a warm, stovetop-and-copper identity, distinct from the
   crimson/gold baby & beauty look.
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:   "#FAF5EC",
  cardBg:   "#FFFFFF",
  headerBg: "#1F3A34",

  black:    "#241E17",
  white:    "#FFFFFF",
  offwhite: "#FAF5EC",
  muted:    "#7A6F60",
  mutedDim: "#A79C8A",

  orange:   "#E3A008", // saffron / turmeric
  red:      "#B23A2E", // brick / braise red

  blue:     "#3B6E64", // sage-teal — used as the 3rd accent in rotations
  blueDim:  "rgba(59,110,100,0.10)",

  cream:    "#F3EAD9",
  peach:    "#EBCBA0",

  border:       "#EBE0CD",
  borderStrong: "rgba(36,30,23,0.22)",

  grad:  "linear-gradient(135deg,#28413B 0%,#152420 100%)",   // pine-teal — used for the header shell
  gradH: "linear-gradient(90deg,#E3A008 0%,#B23A2E 100%)",     // saffron → brick — the "on the stove" accent

  outline: "1.5px solid #EBE0CD",
};

const GRAD   = T.grad;
const GRAD_H = T.gradH;

const RGBA_DARK = (a: number) => `rgba(36,30,23,${a})`;
const RGBA_GOLD = (a: number) => `rgba(227,160,8,${a})`;
const RGBA_RED  = (a: number) => `rgba(178,58,46,${a})`;

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE LOOKUPS (flattened for the variant modal)
═══════════════════════════════════════════════════════════ */
const colorPalette = productColors[0]?.values ?? [];
const sizePalette   = [...Sizes, ...ShoeSizes];

/* ═══════════════════════════════════════════════════════════
   CATEGORY REGISTRY — strictly KitchenCategories
   Each top-level category maps to its own subcategory list,
   derived directly from kitchenSubcategories' parentId —
   no manual per-category array to keep in sync. Same one-layer
   shape BeautyHome/BabyHome use for CAT_SUBCATS.
═══════════════════════════════════════════════════════════ */
const ALL_TOP_CATS: CategoryItem[] = kitchenCategories;

const CAT_SUBCATS: Record<number, CategoryItem[]> = kitchenCategories.reduce(
  (acc, cat) => {
    acc[cat.id] = kitchenSubcategories.filter((s) => s.parentId === cat.id);
    return acc;
  },
  {} as Record<number, CategoryItem[]>
);

/* ═══════════════════════════════════════════════════════════
   BUSINESS / STORE TYPE — Kitchen isn't its own top-level
   business type. It lives under business type "Stores", store
   type "Kitchen Store" — resolved from the shared PartnerCategories
   registry rather than hardcoded, so every partner query on this
   page — local AND global — matches BOTH ids, or the grid will
   pull in non-Kitchen-Store brands. Same exact pattern as
   BeautyHome ("Beauty Store") / BabyHome ("Baby Store").
═══════════════════════════════════════════════════════════ */
const BUSINESS_TYPE_ID = businessType.find((b) => b.name === "Stores")?.id ?? 12;
const STORE_TYPE_ID    = storeType.find((s) => s.name === "Kitchen Store")?.id ?? 1220;

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Karla:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(255,255,255,.55); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes mc-pulse   { 0%,100%{opacity:1} 50%{opacity:.35} }

.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.h-btn{
  display:flex;align-items:center;gap:6px;
  background:rgba(255,255,255,.14);
  border:1px solid rgba(255,255,255,.26);
  cursor:pointer;color:#fff;
  font-size:12px;font-weight:700;
  padding:7px 13px;border-radius:999px;
  flex-shrink:0;white-space:nowrap;
  transition:background .2s;
  font-family:inherit;
}
.h-btn:hover{background:rgba(255,255,255,.24);}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:#FAF5EC; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid rgba(255,255,255,.12); transition:background .15s; }
.nav-item:hover { background: rgba(255,255,255,.08); }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:#B9AE9B; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:#B23A2E; }

.recipe-label { font-family:'Fraunces',serif; font-style:italic; }

.dish-circle { transition: transform .22s cubic-bezier(.34,1.56,.64,1); }
.dish-circle:hover { transform: translateY(-3px) scale(1.05); }

.bsp-plus{ transition:transform .15s; }
.bsp-plus:hover{ transform:scale(1.08); }
.bsp-plus:active{ transform:scale(.92); }

.bsp-opt-row{ transition:border-color .15s, background .15s; }
.bsp-opt-row:hover{ border-color:${RGBA_RED(0.5)}; }
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR STYLES — single expandable top-level category cards
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 20, padding: 18, background: "#fff", marginBottom: 16, boxShadow: `0 2px 16px ${RGBA_GOLD(0.10)}`, border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 20, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: T.mutedDim, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  sidebar:        { background: "rgba(255,255,255,.98)", borderRadius: 22, padding: 20, boxShadow: `0 4px 24px ${RGBA_GOLD(0.12)}`, position: "sticky" as const, top: 86, maxHeight: "calc(100vh - 100px)", overflowY: "auto" as const, border: T.outline },
};

/* page layout styles */
const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Karla',sans-serif", position: "relative" as const },
  content:     { position: "relative" as const, zIndex: 1 },
  headerTop:   { background: T.headerBg, backgroundImage: T.grad, position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_DARK(0.30)}` },
  headerRow:   { display: "flex", alignItems: "center", gap: 10, padding: "14px 16px" },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.16)", borderRadius: 999, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.30)", transition: "border-color .2s,box-shadow .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 16px", fontSize: 14, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 44, minHeight: 42, background: "rgba(255,255,255,.20)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: `0 -4px 24px ${RGBA_GOLD(0.14)}`, zIndex: 100, borderTop: `1px solid ${T.border}` },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK — faded logo tiled across the cream background
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${appLogo})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const KitchenHome: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId ?? "";

  const listings         = useSelector((s: RootState) => s.products.products);
  const filteredListings = useSelector((s: RootState) => (s as any).products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal         = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  const ratingSummaries   = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});

  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const [isMobile,        setIsMobile]        = useState(window.innerWidth <= 768);
  const [catOpen,         setCatOpen]         = useState(false);
  const [desktopCatOpen,  setDesktopCatOpen]  = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchFocused,   setSearchFocused]   = useState(false);

  const [activeCat,           setActiveCat]           = useState<number | null>(null);
  const [activeProductGroup,  setActiveProductGroup]  = useState<number | null>(null);

  const [openCategory,    setOpenCategory]    = useState<string | null>(null);
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry,       setUserCountry]       = useState<string | null>(null);
  const [userCity,          setUserCity]          = useState<string | null>(null);
  const [userLat,           setUserLat]           = useState<number | null>(null);
  const [userLng,           setUserLng]           = useState<number | null>(null);
  const [locationDenied,    setLocationDenied]    = useState(false);
  const [locationLoading,   setLocationLoading]   = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal,        setShowGlobal]        = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [selColor, setSelColor] = useState<number | null>(null);
  const [selSize,  setSelSize]  = useState<number | null>(null);
  const [qty,      setQty]      = useState(1);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantItem, setVariantItem] = useState<any | null>(null);

  const [wished, setWished] = useState<Set<string>>(new Set());

  /* ═══════════════════════════════════════════════════════
     KITCHEN-ONLY PARTNER SET — businessType "Stores" AND
     storeType "Kitchen Store". This is the strict filter:
     everything downstream (brand rows, tier ranking, "only
     kitchen brands" even in Global mode) derives from this
     set, not from all commercePartners. Same pattern as
     BeautyHome / BabyHome.
  ═══════════════════════════════════════════════════════════ */
  const partners = useMemo(() => {
    return (commercePartners || []).filter(
      (p: any) => p.categoryId === BUSINESS_TYPE_ID && p.storeTypeId === STORE_TYPE_ID
    );
  }, [commercePartners]);

  const kitchenBrandIds = useMemo(() => new Set(partners.map((p: any) => p.brandId)), [partners]);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    partners.forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [partners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    partners.forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [partners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners/slices ── */
  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(BUSINESS_TYPE_ID) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  /* Top-level category tap → FILTERS in place (toggle), never navigates.
     Same behaviour as BeautyHome / BabyHome's handleCatSelect. */
  const handleCatSelect = (catId: number) => {
    setActiveCat(prev => {
      const next = prev === catId ? null : catId;
      if (next === null) setActiveProductGroup(null);
      return next;
    });
  };

  const handleProductGroupSelect = (catId: number, groupId: number) => {
    setActiveCat(catId);
    setActiveProductGroup(prev => (prev === groupId ? null : groupId));
    closePanel();
  };

  /* ── cart — single-brand cart, same actions as BeautyHome ── */
  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const getVariantMode = (item: any): "color" | null => {
    if (!item || !item.variants || !Object.keys(item.variants).length) return null;
    return "color";
  };
  const canContinue = getVariantMode(variantItem) === null || (selColor !== null && qty > 0);

  const openProductModal = (item: any) => {
    setVariantItem(item); setSelColor(null); setSelSize(null); setQty(1); setVariantOpen(true);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleRemoveFromCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(decrementCartItem({ listingId: item.listingId }));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartBrandCompat(variantItem.brandId);
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!,
      rate: variantItem.rate!, qty,
      photo: variantItem.variants?.[selColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selColor ?? undefined, sizeId: selSize ?? undefined,
      hostId: variantItem.hostId, brandId: variantItem.brandId,
    } as any));
    setVariantOpen(false); setVariantItem(null); setSelColor(null); setSelSize(null); setQty(1);
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToDetails  = (item: any) => openProductModal(item);
  const goToCheckout = () => navigate(`/kitchencheckout/${userId}`, { state: { items: cartItems } });
  const goToBrand    = (id: string) => navigate(`/StoreHome/${id}`);

  /* ── filtered products — category/group + search + STRICT kitchen-
     brand scoping. kitchenBrandIds is derived from businessType
     "Stores" / storeType "Kitchen Store" partners, so even in
     Global mode only Kitchen Store brands can ever appear here. ── */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? listings).filter((l: any) => {
      if (l.isPublished === false) return false;
      if (!kitchenBrandIds.has(l.brandId)) return false;
      if (activeCat) {
        const matchesCat = l.categoryId === activeCat || l.subcategoryId === activeCat || l.productId === activeCat;
        if (!matchesCat) return false;
      }
      if (activeProductGroup && l.subcategoryId !== activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });

    if (locationActive) {
      list = list.filter((l: any) => {
        const tier = brandTier.get(l.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return list;
  }, [filteredListings, listings, kitchenBrandIds, activeCat, activeProductGroup, searchQuery, showGlobal, brandTier, locationActive]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
    groups = [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return groups;
  }, [filteredProducts, brandTier]);

  /* products bar — a scrollable strip of individual products, not
     categories. Shop by Category + the sidebar/bottom-sheet already
     own category browsing, so this row is a fast, visual product
     scan (real product photos, real names) across whatever's
     currently in view — tapping one FILTERS the store list below to
     that item's brand, exactly like BeautyHome's ProductsIconBar. */
  const scrollProducts = useMemo(() => filteredProducts.slice(0, 24), [filteredProducts]);

  const activeCatName = activeCat ? ALL_TOP_CATS.find(c => c.id === activeCat)?.name ?? null : null;

  /* ═══════════════════════════════════
     HEADER — location · search · categories · cart. Nothing else.
  ═══════════════════════════════════ */
  const HeaderRow = () => (
    <div style={S.headerRow}>
      {locationLoading ? (
        <div className="h-btn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ fontSize: 11, color: "#fff" }}>Locating…</span>
        </div>
      ) : locationDenied ? (
        <button className="h-btn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="h-btn" style={{ background: showGlobal ? "rgba(255,255,255,.10)" : "rgba(255,255,255,.22)" }}>
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={{ ...S.searchWrap, borderColor: searchFocused ? "#fff" : "rgba(255,255,255,.30)" }}>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search cookware, appliances, tools…"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={14} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={17} color="#fff" /></button>
      </div>

      <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)} className="h-btn">
        <LayoutGrid size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="h-btn" style={{ position: "relative", background: cartTotal > 0 ? "rgba(255,255,255,.32)" : "rgba(255,255,255,.16)" }}>
        <ShoppingCart size={18} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, background: "#fff", color: T.red, fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE — SINGLE top-level expandable category cards,
     each expanding to its own subcategory list. Tapping a
     subcategory FILTERS (never navigates).
  ═══════════════════════════════════════════════════════════ */
  const CategoryTree: React.FC<{ category: CategoryItem }> = ({ category }) => {
    const isOpen = openCategory === category.name;
    const subcats = CAT_SUBCATS[category.id] ?? [];
    const isCatSelected = activeCat === category.id && activeProductGroup === null;
    return (
      <div style={A.commerceCard}>
        <div style={{ ...A.commerceHeader, color: isCatSelected ? T.red : T.black }} onClick={() => setOpenCategory(isOpen ? null : category.name)}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {category.name}
            {isCatSelected && <Check size={14} color={T.red} />}
          </span>
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {subcats.map((p) => {
                  const sel = activeProductGroup === p.id;
                  return (
                    <div key={p.id}
                      style={{ ...A.productGroup, color: sel ? T.red : T.mutedDim, fontWeight: sel ? 800 : 600 }}
                      onClick={() => handleProductGroupSelect(category.id, p.id)}>
                      {p.name}
                      {sel && <Check size={12} color={T.red} style={{ marginLeft: "auto" }} />}
                    </div>
                  );
                })}
                {subcats.length === 0 && (
                  <div style={{ ...A.productGroup, color: "#CBBFA9", cursor: "default" }}>No items yet</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      {ALL_TOP_CATS.map((cat) => <CategoryTree key={cat.id} category={cat} />)}
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.5), zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "26px 26px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: RGBA_GOLD(0.32), borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span className="recipe-label" style={{ fontWeight: 700, fontSize: 13, color: T.red }}>Categories</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     PRODUCTS ICON BAR — replaces the old category icon bar. Shop by
     Category (below) plus the sidebar/bottom-sheet already own
     category browsing, so this strip scrolls actual PRODUCTS (real
     photos from redux, not category art), across every category.
     Tapping one opens the product/variant modal — it does NOT
     navigate to CategoryDetails. Same logic as BeautyHome/BabyHome.
  ═══════════════════════════════════ */
  const ProductsIconBar = () => {
    if (scrollProducts.length === 0) return null;
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.gradH, borderRadius: 24, boxShadow: `0 8px 24px ${RGBA_RED(0.20)}` }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {scrollProducts.map((item: any) => (
            <button key={item.listingId}
              onClick={() => goToDetails(item)}
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
              <div className="dish-circle" style={{
                width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                border: "2px solid rgba(255,255,255,.4)", background: "rgba(255,255,255,.15)",
              }}>
                {item.photos?.[0] ? (
                  <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Package size={20} color="#fff" style={{ opacity: 0.75 }} />
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", maxWidth: isMobile ? 60 : 74, overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — group cards, strictly kitchenCategories,
     tiles SELECT a filter (handleCatSelect), no navigation.
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    const chunk = (arr: CategoryItem[], title: string) => {
      const chunks: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) chunks.push(arr.slice(i, i + 4));
      return chunks.map((items, idx) => ({
        title: chunks.length > 1 ? `${title} ${idx + 1}` : title,
        items,
      }));
    };

    const cardGroups = chunk(ALL_TOP_CATS, "Kitchen & Dining");

    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
          <div onClick={() => setShopCatExpanded(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>
              Shop by Category
            </span>
            <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={20} color={T.muted} />
            </motion.div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group) => (
                  <div key={group.title}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 26, overflow: "hidden", border: T.outline, boxShadow: `0 6px 28px ${RGBA_GOLD(0.10)}`, transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${RGBA_RED(0.28)}, 0 16px 38px ${RGBA_GOLD(0.22)}`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 6px 28px ${RGBA_GOLD(0.10)}`; el.style.borderColor = T.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.items.map((cat) => {
                        const Icon = cat.icon;
                        const sel = activeCat === cat.id;
                        return (
                          <button key={cat.id} onClick={() => handleCatSelect(cat.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, borderRadius: isMobile ? 16 : 20, overflow: "hidden" }}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: T.cream, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2 }}>
                              {cat.image ? (
                                <>
                                  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${RGBA_DARK(0.75)},transparent 50%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                                    {cat.name}
                                  </span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  <Icon size={26} color={T.mutedDim} />
                                  <span style={{ fontSize: isMobile ? 9 : 11, fontWeight: 800, color: T.black, textAlign: "center", padding: "0 4px" }}>{cat.name}</span>
                                </div>
                              )}
                              {sel && (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => handleCatSelect(group.items[0].id)}
                      style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: `1.5px solid ${T.border}`, cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.red, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      See all<ArrowRight size={isMobile ? 12 : 14} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW HEADER — clicking navigates to the store via brandId
  ═══════════════════════════════════ */
  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Kitchen Shop";
    const shopLogo = partner?.image || appLogo;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} style={{ background: "none", cursor: "pointer", width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: T.orange, textDecorationThickness: 2, textUnderlineOffset: 4 }}>{shopName}</span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: `0 3px 10px ${RGBA_GOLD(0.20)}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.red} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     CIRCULAR PRODUCT CARD — circle image, white plus-overlay
     button, name + price pill below. Items with colour/size
     variants open the modal instead of adding directly. Same
     card UI/logic as BeautyHome's / BabyHome's CircularProductCard.
  ═══════════════════════════════════ */
  const CircularProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const hasVariants = getVariantMode(item) !== null;
    const q = hasVariants ? 0 : getQty(item.listingId);
    const price = `${symbol ?? "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const isWished  = wished.has(item.listingId);
    const discount  = item.discount ?? (item.compareAtRate && item.compareAtRate > (item.rate ?? 0)
      ? Math.round(((item.compareAtRate - (item.rate ?? 0)) / item.compareAtRate) * 100) : null);

    const size = isMob ? 110 : 140;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <button onClick={() => goToDetails(item)}
            className="dish-circle"
            style={{
              width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden",
              border: "2px solid rgba(178,58,46,.15)", background: T.cream, position: "relative",
              boxShadow: `0 4px 14px ${RGBA_GOLD(0.15)}`, cursor: "pointer", padding: 0,
            }}>
            {item.photos?.[0] ? (
              <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Package size={isMob ? 30 : 40} color={T.orange} style={{ opacity: 0.4 }} />
              </div>
            )}
          </button>

          {/* Wishlist */}
          <button onClick={e => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: 2, left: 2, background: "rgba(255,255,255,.92)", border: "none", width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 2px 6px ${RGBA_DARK(0.14)}`, zIndex: 6 }}>
            <Heart size={11} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {/* White plus button overlay */}
          <motion.button className="bsp-plus"
            whileTap={{ scale: 0.92 }}
            onClick={e => {
              e.stopPropagation();
              if (hasVariants) { goToDetails(item); return; }
              if (q === 0) handleAddToCart(item, e); else goToDetails(item);
            }}
            style={{
              position: "absolute", bottom: -6, right: -6, width: isMob ? 36 : 44, height: isMob ? 36 : 44,
              borderRadius: "50%", border: "none", background: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 4px 12px ${RGBA_DARK(0.22)}`, zIndex: 10,
            }}>
            <Plus size={isMob ? 18 : 22} color={T.red} />
          </motion.button>

          {discount && (
            <div style={{ position: "absolute", top: 4, right: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, zIndex: 5 }}>
              -{discount}%
            </div>
          )}

          {hasRating && (
            <div style={{ position: "absolute", top: discount ? 26 : 4, right: 4, zIndex: 6, background: "rgba(255,255,255,.92)", borderRadius: 999, padding: "2px 6px", display: "flex", alignItems: "center", gap: 2 }}>
              <Star size={9} fill={T.orange} color={T.orange} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: 9 }}>{summary!.avgRating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", width: "100%", minWidth: 0 }}>
          <div style={{ fontSize: isMob ? 11 : 12, fontWeight: 700, color: T.black, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 2 }}>
            {item.name}
          </div>
          <div style={{ fontSize: isMob ? 10 : 11, fontWeight: 900, color: T.red, background: RGBA_RED(0.10), padding: "2px 6px", borderRadius: 999, display: "inline-block" }}>
            {hasVariants ? "From " : ""}{price}
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SORRY / NO SHOPS FOUND
  ═══════════════════════════════════ */
  const SorryNoStores = ({ categoryName }: { categoryName: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>🍽️</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>Sorry, no {categoryName} shops found near you</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Try another category, or switch to global mode.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCT ROWS — grouped by brand, horizontal scroll of circular
     cards, instead of a fixed grid. Same layout as BeautyHome/BabyHome.
  ═══════════════════════════════════ */
  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeCatName ?? (locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: RGBA_GOLD(0.12), padding: "2px 9px", borderRadius: 999 }}>{filteredProducts.length}</span>
        </div>
        {locationActive && (
          <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#fff" : RGBA_GOLD(0.12), borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? T.border : RGBA_RED(0.3)}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
            {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
          </button>
        )}
      </div>

      {activeCatName && productsByBrand.length === 0 ? (
        <SorryNoStores categoryName={activeCatName} />
      ) : filteredProducts.length > 0 ? (
        productsByBrand.map(group => (
          <div key={group.brandId} style={{ marginBottom: 20 }}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={{ display: "flex", gap: isMob ? 12 : 16, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 8 }}>
              {group.items.map((item: any) => <CircularProductCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>🍽️</div>
          <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>{locationDenied ? "Location needed" : "No pieces found"}</h3>
          <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local kitchen shops." : "No products match your current filters."}</p>
          <button onClick={() => locationDenied ? setLocationModalOpen(true) : resetAllFilters()}
            style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
            {locationDenied ? "Enable Location" : "View All Products"}
          </button>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     VARIANT MODAL — colour + size + qty, Add to Cart / Buy Now.
  ═══════════════════════════════════ */
  const VariantModal = () => {
    if (!variantOpen || !variantItem) return null;
    const variantColorIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const sizeIds: number[] = selColor && variantItem.variants?.[selColor]?.sizeIds ? variantItem.variants[selColor].sizeIds : [];
    const previewImg = selColor && variantItem?.variants?.[selColor]?.imageUrl ? variantItem.variants[selColor].imageUrl : variantItem?.photos?.[0];

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "28px 28px 0 0" : 24, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: RGBA_GOLD(0.24), margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg && (
            <div style={{ width: "100%", aspectRatio: "16/7", borderRadius: 18, overflow: "hidden", marginBottom: 16, background: T.cream }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{variantItem?.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 18 }}>
            {symbol ?? "€"}{((variantItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}
          </div>

          {variantColorIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 12 }}>
                COLOUR
                {selColor && (() => {
                  const cv = colorPalette.find(c => c.id === selColor);
                  return cv ? <span style={{ fontWeight: 600, color: T.red, letterSpacing: "0.06em", textTransform: "none", fontSize: 12 }}> — {cv.name}</span> : null;
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(64px,1fr))", gap: 10, marginBottom: 20 }}>
                {variantColorIds.map(cId => {
                  const cv = colorPalette.find(c => c.id === cId);
                  const hex = (cv as any)?.hex ?? (cv as any)?.hexCodes?.[0];
                  const isSel = selColor === cId;
                  return (
                    <button key={cId} onClick={() => { setSelColor(cId); setSelSize(null); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                      <div style={{ position: "relative", width: 40, height: 40 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%",
                          background: hex ? sphere3D(hex) : sphere3D(T.orange),
                          boxShadow: `inset -6px -5px 12px ${RGBA_DARK(0.30)},inset 5px 5px 10px rgba(255,255,255,.22),0 5px 18px ${RGBA_GOLD(0.22)}${isSel ? ",0 0 0 2.5px " + T.red : ""}`,
                          outline: isSel ? `2.5px solid ${T.red}` : "none", outlineOffset: 3,
                        }} />
                        {isSel && (
                          <div style={{ position: "absolute", bottom: -3, right: -3, width: 16, height: 16, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }}>
                            <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: isSel ? 700 : 500, color: isSel ? T.red : T.mutedDim, textAlign: "center", maxWidth: 60, wordBreak: "break-word" as const, lineHeight: 1.2 }}>{cv?.name ?? String(cId)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selColor && sizeIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 12 }}>SIZE</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {sizeIds.map((sId: number) => {
                  const szv = sizePalette.find(s => s.id === sId);
                  const isSel = selSize === sId;
                  return (
                    <button key={sId} onClick={() => setSelSize(sId)}
                      style={{ padding: "8px 16px", border: `1.5px solid ${isSel ? T.red : T.border}`, background: isSel ? T.red : "#fff", color: isSel ? "#fff" : T.black, fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 8, transition: "all .15s" }}>
                      {szv?.name ?? sId}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 14, overflow: "hidden", background: T.pageBg }}>
              <button onClick={() => setQty(v => Math.max(1, v - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(v => v + 1)} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 14, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Cart
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 14, border: "none", background: canContinue ? GRAD_H : "#E5DAC4", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => { setVariantOpen(false); setVariantItem(null); setSelColor(null); }}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.55), zIndex: 500, backdropFilter: "blur(6px)" }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={e => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${RGBA_RED(0.26)}` }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL
  ═══════════════════════════════════ */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: RGBA_DARK(0.6), zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 24, padding: 24, background: "#fff", boxShadow: `0 30px 80px ${RGBA_RED(0.28)}`, textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local kitchen shops.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     MOBILE BOTTOM TABS
  ═══════════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: true, action: () => navigate(`/KitchenHome/${userId}`) },
        { icon: <ChefHat size={22} />, label: "Shops", active: false, action: () => navigate("/Stores") },
        { label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartItems.reduce((s: number, i: any) => s + i.qty, 0)}</span>}
            </div>
          ), action: goToCheckout },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/kitchenclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : "#B9AE9B" })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <div style={S.headerTop}>
          <HeaderRow />
        </div>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <CategoryPanelContent />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <ProductsIconBar />
              <ShopByCategory />

              {activeCat != null && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.06), borderRadius: 14, border: `1.5px solid ${T.red}` }}>
                  <Tag size={16} color={T.red} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                  <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={12} color={T.red} />
                  </button>
                </motion.div>
              )}

              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <ProductsIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>

            {activeCat != null && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: RGBA_RED(0.06), borderRadius: 14, border: `1.5px solid ${T.red}` }}>
                <Tag size={16} color={T.red} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.red, flex: 1 }}>Browsing: {activeCatName}</span>
                <button onClick={() => { setActiveCat(null); setActiveProductGroup(null); }} style={{ background: "#fff", border: "none", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} color={T.red} />
                </button>
              </motion.div>
            )}

            {renderProducts(true)}
          </div>
        )}

        <VariantModal />
        <LocationModal />

        <BottomTabs />
      </div>
    </div>
  );
};

export default KitchenHome;